import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useFetch, useNuxtApp, useRoute } from '#app';
import { BigNumber } from 'bignumber.js';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { StakeModel, TStakeResponse } from '~/types/apiService';
import { apiUrls, Tokens } from '~/utils/constants';
import useEnvs from '~/composables/useEnvs';

export default (token: Tokens) => {
  const { address } = useWeb3ModalAccount();
  const { $errorHandler } = useNuxtApp();
  const route = useRoute();
  const store = useMainStore();
  const unstakedCookie = useCookie<string[]>('unstaked');
  const { unlockable, lockPeriod, unlockPeriod } = useTokensReader();
  const { blockchain, apiUrl } = useEnvs();

  const REFRESH_INTERVAL_MS = 8000;
  let refreshInterval: ReturnType<typeof setInterval> | null = null;
  const isTabActive = ref(true);

  // indicates which stakeIdx is waiting for updated data
  const waitingStakeIdx = ref<number>();
  const isLoading = ref(false);
  const lockedPeriodRef = ref('');
  const unlockPeriodRef = ref('');
  const unlockableRef = ref<string[]>([]);

  const currentTokenAddress = computed(() => blockchain.contracts[token].addresses.contract);

  const {
    data,
    refresh: refreshStakeData,
    status: _stakeDataStatus
  } = useFetch<TStakeResponse>(apiUrls.v1.stake, {
    baseURL: apiUrl,
    query: {
      token: currentTokenAddress,
      sender: address
    },
    server: false,
    pick: ['items'],
    watch: [currentTokenAddress, address],
    immediate: !!address.value,
    key: `/v1/stake${currentTokenAddress.value}`
  });

  // Флаг первой загрузки для предотвращения мерцания таблицы при обновлении
  const isFirstLoading = ref(true);

  // Сбрасываем флаг первой загрузки после успешной загрузки
  watch(_stakeDataStatus, (status) => {
    if (status === 'success' && isFirstLoading.value) {
      isFirstLoading.value = false;
    }
  });

  // Сбрасываем флаг первой загрузки при смене токена или адреса
  watch(
    () => [token, address.value],
    () => {
      isFirstLoading.value = true;
    }
  );

  // Функции для управления интервалом обновления
  const startRefreshing = (): void => {
    if (refreshInterval || route.path !== '/dashboard') {
      return;
    }

    refreshInterval = setInterval(() => {
      if (isTabActive.value && route.path === '/dashboard' && address.value) {
        refreshStakeData();
      }
    }, REFRESH_INTERVAL_MS);
  };

  const stopRefreshing = (): void => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  };

  // Обработчик изменения видимости страницы
  const handleVisibilityChange = (): void => {
    const isVisible = document.visibilityState === 'visible';
    isTabActive.value = isVisible;

    if (isVisible) {
      startRefreshing();
    } else {
      stopRefreshing();
    }
  };

  // Запускаем обновление при монтировании и при изменении адреса
  watch(
    () => address.value,
    (newAddress) => {
      if (newAddress && route.path === '/dashboard') {
        startRefreshing();
        // Добавляем обработчик изменения видимости страницы только один раз
        if (import.meta.client && !(window as any).__stakeRefreshListenerAdded) {
          (window as any).__stakeRefreshListenerAdded = true;
          document.addEventListener('visibilitychange', handleVisibilityChange);
        }
      } else {
        stopRefreshing();
      }
    },
    { immediate: true }
  );

  // Останавливаем обновление при размонтировании
  onBeforeUnmount(() => {
    stopRefreshing();
  });

  watch(
    address,
    async (newValue) => {
      if (newValue && data.value && data.value.items.length > 0) {
        await updateData();
      }
    },
    {
      immediate: true
    }
  );

  watch(data, async (newValue, oldValue) => {
    if (data.value && data.value.items.length > 0) {
      checkIsUnstaked(data.value.items);
      await updateData();
    }

    // Правильное сравнение: ищем изменения по stakeIdx, а не по индексу массива
    if (newValue?.items && oldValue?.items) {
      const hasClaimedChanged = newValue.items.some((newItem) => {
        const oldItem = oldValue.items.find((item) => item.stakeIdx === newItem.stakeIdx);
        return oldItem && String(oldItem.claimed) !== String(newItem.claimed);
      });

      if (hasClaimedChanged) {
        waitingStakeIdx.value = -1;
      }
    }
  });

  // Обновляем данные после покупки (когда изменяется notificationUpdateVersion)
  watch(
    () => store.notificationUpdateVersion,
    async () => {
      if (address.value && token === Tokens.pearl) {
        // Ждем немного, чтобы API успел обработать транзакцию покупки
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Обновляем данные из API
        await refreshStakeData();

        // Ждем немного, чтобы данные успели обновиться
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Обновляем локальные данные (даже если список был пуст, после покупки может появиться новый элемент)
        if (data.value) {
          await updateData();
        }
      }
    }
  );

  async function updateData(): Promise<void> {
    isLoading.value = true;

    const tmp: string[] = [];

    try {
      if (data.value && data.value.items) {
        const promises = data.value.items.map(async (rec) => {
          return await unlockable(address.value as string, rec.stakeIdx, token);
        });

        const settled = await Promise.allSettled(promises);
        // TODO: test it, if it works add types
        settled.forEach((res: any, i: number) => {
          // since this list is based on list.value.items just use index
          const rec = data.value?.items[i];
          if (rec) {
            if (!rec.released && address.value && res.status === 'fulfilled') {
              tmp[rec.stakeIdx] = res.value || '0';
            } else {
              tmp[rec.stakeIdx] = '';
            }
          }
        });
      }

      const [lockedPeriodValue, unlockPeriodValue] = await Promise.allSettled([
        lockPeriod(token),
        unlockPeriod(token)
      ]).then((results) => results.map((res) => (res.status === 'fulfilled' ? res.value : null)));

      lockedPeriodRef.value = (lockedPeriodValue || '0').toString();
      unlockPeriodRef.value = (unlockPeriodValue || '0').toString();
      unlockableRef.value = tmp;
    } catch (err: unknown) {
      if (typeof err === 'string') {
        $errorHandler(err);
      }

      console.log(err);
    } finally {
      isLoading.value = false;
    }
  }

  function getUnlockableValue(index: number) {
    const value = unlockableRef.value[index];
    if (value === undefined || value === '') {
      return '';
    }
    return BigNumber(value).toFormat(2);
  }

  async function updateAfterUnstake(stakeIdx: number) {
    waitingStakeIdx.value = stakeIdx;
    const itemForUnstake = data.value?.items.find((item) => item.stakeIdx === stakeIdx);
    const previousClaimed = itemForUnstake?.claimed;

    useTrackEvent('stake_finish', {
      stakeId: stakeIdx,
      userId: address.value,
      tokenAmount: itemForUnstake?.amount,
      rewardsReceived: itemForUnstake?.claimed
    });

    // Обновляем данные из API после разблокировки с повторными попытками
    const maxRetries = 5;
    const initialDelay = 2000; // 2 секунды перед первой попыткой
    const retryDelay = 2000; // 2 секунды между попытками

    // Ждем перед первой попыткой, чтобы API успел обработать транзакцию
    await new Promise((resolve) => setTimeout(resolve, initialDelay));

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      // Ждем между попытками (кроме первой)
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }

      // Обновляем данные из API
      await refreshStakeData();

      // Ждем немного, чтобы данные успели обновиться
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Проверяем, обновились ли данные
      const updatedItem = data.value?.items.find((item) => item.stakeIdx === stakeIdx);

      if (updatedItem) {
        // Если данные обновились (released стал true или claimed изменился), прекращаем попытки
        const isReleased = updatedItem.released === true;
        const claimedChanged =
          previousClaimed !== undefined &&
          updatedItem.claimed !== undefined &&
          String(updatedItem.claimed) !== String(previousClaimed);

        if (isReleased || claimedChanged) {
          // Сбрасываем waitingStakeIdx после успешного обновления
          waitingStakeIdx.value = undefined;
          break;
        }
      }
    }

    // Обновляем локальные данные после обновления из API
    await updateData();

    // Сбрасываем waitingStakeIdx в любом случае после завершения обновления
    waitingStakeIdx.value = undefined;
  }

  function checkIsUnstaked(dataItems: StakeModel[]) {
    unstakedCookie.value.forEach((item) => {
      const unstakeClickedItem = item.split('_');
      const [stakeId] = unstakeClickedItem;

      const unstakedItem = dataItems.find((item: StakeModel) => item.stakeIdx === Number(stakeId));

      if (unstakedItem && unstakedItem.released) {
        unstakedCookie.value = unstakedCookie.value.filter((unstakedItem) => unstakedItem !== item);
      }
    });
  }

  return {
    lockedPeriodRef,
    getUnlockableValue,
    stakeData: data,
    updateAfterUnstake,
    waitingStakeIdx
  };
};
